import { useCallback, useEffect, useState } from "react";
import {
  createManufacturingLaborRateItem,
  deleteManufacturingLaborRateItem,
  listManufacturingLaborRateItems,
  updateManufacturingLaborRateItem,
  type MfgLaborRateItem,
} from "@/lib/manufacturing/laborBillingClient";
import { normalizeLaborMatchKey } from "@/lib/manufacturing/laborCompanyPayMatch";
import type { LaborPaysBy } from "@/lib/manufacturing/laborPayQuantity";
import {
  laborRateImportParkableRows,
  laborRateImportUnmatchedItemCode,
  type LaborRateImportRow,
} from "@/lib/manufacturing/laborCompanyRateImport";

export type CompanyRateDraft = {
  recipe_id: string;
  item_code: string;
  label: string;
  option_code: string;
  flat_rate_usd: string;
  notes: string;
  searchHint: string;
  pays_by: LaborPaysBy | "";
};

export const EMPTY_COMPANY_RATE: CompanyRateDraft = {
  recipe_id: "",
  item_code: "",
  label: "",
  option_code: "",
  flat_rate_usd: "",
  notes: "",
  searchHint: "",
  pays_by: "",
};

export function draftFromUnmatchedLine(line: {
  option_key: string;
  key: string;
  label: string;
}): CompanyRateDraft {
  const match = line.option_key || line.key || normalizeLaborMatchKey(line.label);
  return {
    recipe_id: "",
    item_code: "",
    label: line.label || match,
    option_code: "",
    flat_rate_usd: "0",
    notes: "",
    searchHint: match,
    pays_by: "",
  };
}

export function useManufacturingLaborCompanyRates(enabled: boolean, navTick: number) {
  const [items, setItems] = useState<MfgLaborRateItem[]>([]);
  const [listBusy, setListBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<CompanyRateDraft>(EMPTY_COMPANY_RATE);

  const reload = useCallback(async () => {
    if (!enabled) return;
    setListBusy(true);
    setError(null);
    const res = await listManufacturingLaborRateItems();
    setListBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setItems(res.data.items);
  }, [enabled]);

  useEffect(() => {
    void reload();
  }, [reload, navTick]);

  const startCreate = useCallback((seed?: CompanyRateDraft) => {
    setEditingId(null);
    setDraft(seed ?? EMPTY_COMPANY_RATE);
    setShowForm(true);
  }, []);

  const startEdit = useCallback((item: MfgLaborRateItem) => {
    setEditingId(item.id);
    setDraft({
      recipe_id: item.recipe_id ?? "",
      item_code: item.item_code,
      label: item.label,
      option_code: item.option_code,
      flat_rate_usd: String(item.flat_rate_usd),
      notes: item.notes,
      searchHint: item.option_code || item.item_code,
      pays_by: item.pays_by ?? "",
    });
    setShowForm(true);
  }, []);

  const cancel = useCallback(() => {
    setShowForm(false);
    setEditingId(null);
    setDraft(EMPTY_COMPANY_RATE);
  }, []);

  const submit = useCallback(async () => {
    const rateRaw = draft.flat_rate_usd.trim();
    const recipe_id = draft.recipe_id.trim();
    const option_code = draft.option_code.trim();
    const item_code = draft.item_code.trim() || option_code;
    const label = draft.label.trim() || item_code;
    const flat_rate_usd = Number(rateRaw);
    const pays_by = draft.pays_by;
    if (!recipe_id || !item_code) {
      setError("Pick an existing Models SKU.");
      return false;
    }
    if (!pays_by) {
      setError("Choose Pays by. Do not assume quantity 1.");
      return false;
    }
    if (!rateRaw || !Number.isFinite(flat_rate_usd) || flat_rate_usd < 0) {
      setError("Enter $ rate (0 is allowed).");
      return false;
    }
    setActionBusy(true);
    setError(null);
    const res = editingId
      ? await updateManufacturingLaborRateItem({
          rate_item_id: editingId,
          recipe_id,
          item_code,
          label,
          option_code,
          flat_rate_usd,
          notes: draft.notes.trim(),
          pays_by,
        })
      : await createManufacturingLaborRateItem({
          recipe_id,
          item_code,
          label,
          option_code,
          flat_rate_usd,
          notes: draft.notes.trim(),
          pays_by,
        });
    setActionBusy(false);
    if (!res.ok) {
      setError(res.error);
      return false;
    }
    cancel();
    await reload();
    return true;
  }, [cancel, draft, editingId, reload]);

  const toggleActive = useCallback(
    async (item: MfgLaborRateItem) => {
      setActionBusy(true);
      setError(null);
      const res = await updateManufacturingLaborRateItem({
        rate_item_id: item.id,
        is_active: !item.is_active,
      });
      setActionBusy(false);
      if (!res.ok) {
        setError(res.error);
        return;
      }
      await reload();
    },
    [reload]
  );

  const remove = useCallback(
    async (itemId: string) => {
      setActionBusy(true);
      setError(null);
      const res = await deleteManufacturingLaborRateItem({ rate_item_id: itemId });
      setActionBusy(false);
      if (!res.ok) {
        setError(res.error);
        return;
      }
      await reload();
    },
    [reload]
  );

  const saveImported = useCallback(
    async (
      rows: readonly LaborRateImportRow[],
      opts?: { parkSuggestedLeftovers?: boolean }
    ): Promise<{ ok: true } | { ok: false; error: string }> => {
      const accepted = opts?.parkSuggestedLeftovers
        ? []
        : rows.filter((r) => r.status === "accepted" && r.recipe && r.rate_usd != null);
      const unmatched = laborRateImportParkableRows(rows, Boolean(opts?.parkSuggestedLeftovers));
      setActionBusy(true);
      setError(null);
      for (const row of accepted) {
        const recipe = row.recipe!;
        const label = row.label.trim() || recipe.name || recipe.code;
        const payload = {
          recipe_id: recipe.id,
          item_code: recipe.code,
          option_code: recipe.code,
          label,
          flat_rate_usd: row.rate_usd as number,
          notes: row.notes.trim(),
          pays_by: row.pays_by,
        };
        const res = row.existing_rate_id
          ? await updateManufacturingLaborRateItem({ rate_item_id: row.existing_rate_id, ...payload })
          : await createManufacturingLaborRateItem(payload);
        if (!res.ok) {
          setActionBusy(false);
          setError(res.error);
          return { ok: false, error: res.error };
        }
      }
      for (const row of unmatched) {
        const item_code = laborRateImportUnmatchedItemCode(row);
        const existing = items.find((item) => item.item_code.toLowerCase() === item_code.toLowerCase());
        if (existing?.recipe_id) continue;
        const payload = {
          recipe_id: null as string | null,
          item_code,
          option_code: item_code,
          label: row.label.trim() || row.name.trim() || item_code,
          flat_rate_usd: row.rate_usd,
          notes: row.notes.trim(),
          pays_by: row.pays_by,
        };
        const res = existing
          ? await updateManufacturingLaborRateItem({ rate_item_id: existing.id, ...payload })
          : await createManufacturingLaborRateItem(payload);
        if (!res.ok) {
          setActionBusy(false);
          setError(res.error);
          return { ok: false, error: res.error };
        }
      }
      setActionBusy(false);
      await reload();
      return { ok: true };
    },
    [items, reload]
  );

  return {
    items,
    listBusy,
    actionBusy,
    error,
    showForm,
    editingId,
    draft,
    setDraft,
    startCreate,
    startEdit,
    cancel,
    submit,
    toggleActive,
    remove,
    reload,
    saveImported,
  };
}
